import { ReactNode, createContext, useContext, useState } from "react";

type VisibleLinesType = {
  totalUsers: boolean;
  registeredUsers: boolean;
  guestUsers: boolean;
  newRegisteredUsers: boolean;
  newGuestUsers: boolean;
  [key: string]: boolean;
};

type ReportsContextType = {
  dateRange: string;
  setDateRange: (range: string) => void;
  apiDateRange: { start_date: string; end_date: string };
  setApiDateRange: (range: { start_date: string; end_date: string }) => void;
  activeTab: 'sessions' | 'users';
  setActiveTab: (tab: 'sessions' | 'users') => void;
  metrics: any[];
  chartData: any[];
  isLoading: boolean;
  showMultipleLines: boolean;
  setShowMultipleLines: (show: boolean) => void;
  visibleLines: VisibleLinesType;
  setVisibleLines: (lines: VisibleLinesType) => void;
};

const ReportsContext = createContext<ReportsContextType | undefined>(undefined);

export const useReports = () => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error('useReports must be used within ReportsProvider');
  }
  return context;
};

export const ReportsProvider = ({ children }: { children: ReactNode }) => {
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    const formatDate = (date: Date) => {
      const day = date.getDate();
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[date.getMonth()];
      const year = date.getFullYear().toString().slice(-2);
      return `${day} ${month} ${year}`;
    };
    
    return `${formatDate(thirtyDaysAgo)} — ${formatDate(today)}`;
  });
  const [apiDateRange, setApiDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 30);
    return {
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0]
    };
  });
  const [activeTab, setActiveTab] = useState<'sessions' | 'users'>('sessions');
  const [metrics] = useState([]);
  const [chartData] = useState([]);
  const [isLoading] = useState(false);
  const [showMultipleLines, setShowMultipleLines] = useState(false);
  const [visibleLines, setVisibleLines] = useState<VisibleLinesType>({
    totalUsers: true,
    registeredUsers: true,
    guestUsers: false,
    newRegisteredUsers: false,
    newGuestUsers: false
  });

  return (
    <ReportsContext.Provider value={{
      dateRange,
      setDateRange,
      apiDateRange,
      setApiDateRange,
      activeTab,
      setActiveTab,
      metrics,
      chartData,
      isLoading,
      showMultipleLines,
      setShowMultipleLines,
      visibleLines,
      setVisibleLines
    }}>
      {children}
    </ReportsContext.Provider>
  );
};